import React, { useState } from "react";

import Layout from "../components/layout";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserTie, faCrosshairs, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { faLightbulb, faHandshake } from '@fortawesome/free-regular-svg-icons';
import { CSSTransition } from 'react-transition-group';
import "../styles/sass/main.scss";

const IndexPage = () => {
  const [paragraphOneOpen, setParagraphOneOpen] = useState(false);
  const [paragraphTwoOpen, setParagraphTwoOpen] = useState(false);

  return (
    <Layout>
      <section className="section-slogan">
        <div className="section-slogan__text-box">
          <h1 className="heading-primary">
            <span className="heading-primary--main">In a Code Blue</span>
            <span className="heading-primary--sub">every second counts!</span>
          </h1>

          <a href="#section-products" className="btn btn--white btn--animated">
            Discover our products
        </a>
        </div>
      </section>

      <section className="section-about">
        <div className="u-center-text u-margin-bottom-big">
          <h2 className="heading-secondary">Inspiration</h2>
        </div>

        <div className="row">
          <div className="col-1-of-2">
            <h3 className="heading-tertiary u-margin-bottom-small u-color-primary"><a href="https://www.medicinenet.com/script/main/art.asp?articlekey=59953" target="_blank">What is a Code Blue?</a></h3>
            <p className="paragraph u-margin-bottom-zero">
              Code blue is an emergency situation announced in a hospital or institution in which a patient is
              in cardiopulmonary arrest, requiring a team of providers (sometimes called a &#39;code team&#39;) to
              rush to the specific location and begin immediate cardiopulmonary resuscitation (CPR).
              These situations are dire emergencies. Medical errors are likely to occur, and lives can be lost. (
              <a href="https://www.youtube.com/watch?v=4EWlNpKrCuc&feature=youtu.be&ab_channel=MasterTrainInc" target="blank" className="u-color-primary">
                  Code Blue in a Hospital
              </a>
              )
            </p>
              
            <CSSTransition in={paragraphOneOpen} timeout={500} classNames="paragraph__animation" unmountOnExit>
              <div>
                <h3 className="heading-tertiary u-margin-bottom-small u-color-primary u-margin-top-small">Code Blue Documentation and Debriefing</h3>
                <p className='paragraph u-margin-bottom-zero u-margin-top-small'>
                  The current standard of code blue documentation is using handwritten records on a paper “code
                  sheet”. Post-CPR debriefings and feedback are essential for identifying errors and improving
                  performance. Reviewing the performance quality of the “code blue team” is a cornerstone for
                  improving outcomes of in-hospital arrests. Thorough and accurate recording of code blue events
                  facilitates the detailed analyses needed for quality improvement. However, using code sheet
                  recording is subject to human errors and does not include sufficient details to allow
                  measurement of key quality indicators.  
                </p>
              </div>
            </CSSTransition>
            <div className="row" style={{ justifyContent: "center" }}>
              <CSSTransition in={paragraphOneOpen} timeout={500} classNames="paragraph__icon">
                <FontAwesomeIcon icon={faAngleDown} onClick={() => setParagraphOneOpen(!paragraphOneOpen)} size="5x" />
              </CSSTransition>
            </div>
          </div>
          <div className="col-1-of-2">
            <div className="youtube-video__wrapper">
              <iframe width="560" height="315" src="https://www.youtube.com/embed/4EWlNpKrCuc" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-1-of-2">
            <h3 className="heading-tertiary u-margin-bottom-small u-color-primary">
              Pediatric Asthma
            </h3>
            <p className="paragraph u-margin-bottom-zero">
              In the U.S., 7.1 million children under 18 years old, almost 1 in 11 children, suffer from asthma.
              Asthma is the most prevalent chronic disease of childhood. Asthma exacerbations lead to
              emergency room visits, hospitalizations, school absence, and parental work disruption. The
              estimated economic loss is $18 billion annually.
            </p>
            <CSSTransition in={paragraphTwoOpen} timeout={500} classNames="paragraph__animation" unmountOnExit>
              <p className='paragraph u-margin-bottom-zero u-margin-top-small'>
                To assess asthma control, clinicians rely on information on the frequency and severity of
                symptoms in the preceding weeks. Patient and parent recall of symptoms is often inaccurate
                and biased. Many questionnaire-based tools have been designed to focus on asthma control in
                the short-term, but these tools remain subject to biases and inaccuracy.
              </p>
            </CSSTransition>
            <div className="row" style={{ justifyContent: "center" }}>
              <CSSTransition in={paragraphTwoOpen} timeout={500} classNames="paragraph__icon">
                <FontAwesomeIcon icon={faAngleDown} onClick={() => setParagraphTwoOpen(!paragraphTwoOpen)} size="5x" />
              </CSSTransition>
            </div>
          </div>
          <div className="col-1-of-2">
            <div className="youtube-video__wrapper">
              <iframe width="560" height="315" src="https://www.youtube.com/embed/s2WCWIE9TcI" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
            </div>
          </div>
        </div>
      </section>

      <section className="section-core-values">
        <div className="row">
          <div className="col-1-of-4">
            <div className="feature-box">
              <div className="feature-box__icon">
                <FontAwesomeIcon icon={faLightbulb} />
              </div>
              <h3 className="heading-tertiary u-margin-bottom-small">
                Innovation
            </h3>
              <p className="feature-box__text">
                Be creative, open-minded and drive change with vigor.
            </p>
            </div>
          </div>

          <div className="col-1-of-4">
            <div className="feature-box">
              <div className="feature-box__icon">
                <FontAwesomeIcon icon={faUserTie} />
              </div>
              <h3 className="heading-tertiary u-margin-bottom-small">
                Professionalism
            </h3>
              <p className="feature-box__text">
                Pursue efficiency and precision.<br /><br />
              </p>
            </div>
          </div>

          <div className="col-1-of-4">
            <div className="feature-box">
              <div className="feature-box__icon">
                <FontAwesomeIcon icon={faCrosshairs} />
              </div>
              <h3 className="heading-tertiary u-margin-bottom-small">
                User Focused
            </h3>
              <p className="feature-box__text">
                Listen and respond positively to users’ needs.
              </p>
            </div>
          </div>

          <div className="col-1-of-4">
            <div className="feature-box">
              <div className="feature-box__icon">
                <FontAwesomeIcon icon={faHandshake} />
              </div>
              <h3 className="heading-tertiary u-margin-bottom-small">
                Collaboration
            </h3>
              <p className="feature-box__text">
                Build partnerships internally and externally
            </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-products" id="section-products">
        <div className="u-center-text u-margin-bottom-big">
          <h2 className="heading-secondary">Our products</h2>
        </div>
        <div className="row">
          <div className="col-1-of-2">
            <div className="card">
              <div className="card__side">
                <div className="card__picture card__picture--bluebox">&nbsp;</div>
                <h4 className="card__heading">
                  <span className="card__heading-span card__heading-span--1">
                    BlueBox
                </span>
                </h4>
                <div className="card__details">
                  <ul>
                    <li>
                      Fast, complete and accurate recording of code blue events.
                  </li>
                    <li>Visualize patient vital signs on iPad application .</li>
                    <li>Generate electrical code sheet.</li>
                    <li>Easy and safe data transmission and storage.</li>
                    <li>Support medical training and quality improvement.</li>
                  </ul>
                </div>
                <div className="card__button">
                  <a
                    href="/products/bluebox"
                    className="btn btn--primary"
                    style={{ margin: "0 auto" }}
                  >
                    More Details!
                </a>
                </div>
              </div>
            </div>
          </div>

          <div className="col-1-of-2">
            <div className="card">
              <div className="card__side">
                <div className="card__picture card__picture--asthmagram">
                  &nbsp;
              </div>
                <h4 className="card__heading">
                  <span className="card__heading-span card__heading-span--2">
                    Asthmagram
                </span>
                </h4>
                <div className="card__details">
                  <ul>
                    <li>
                      Comfortable, non-intrusive,, water-proof wearable device.
                  </li>
                    <li>
                      24/7 monitoring breathing, muscle tension and posture.
                  </li>
                    <li>Instant alert of Asthma symptoms from mobile phone.</li>
                    <li>
                      Automatically generate Asthma report with statistical data.
                  </li>
                    <li>Aim to assess asthma control by clinicians.</li>
                  </ul>
                </div>
                <div className="card__button">
                  <a href="/products/asthmagram" className="card__button btn btn--secondary">
                    More Details!
                </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* <div className="u-center-text u-margin-top-huge">
        <a href="#" className="btn btn--green">
          Discover all products
        </a>
      </div> */}
      </section>

      {/* <section className="section-news">
      <div className="u-center-text u-margin-bottom-big">
        <h2 className="heading-secondary">News</h2>
      </div>

      <div className="row">
        <div className="news">
          <div className="news__text">
            <h3 className="heading-tertiary u-margin-bottom-small">
              I had the best week ever with my family
            </h3>
            <p>
              Lorem ipsum dolor sit amet consectetur adipisicing elit. Aperiam,
              ipsum sapiente aspernatur libero repellat quis consequatur ducimus
              quam nisi exercitationem omnis earum qui. Aperiam, ipsum sapiente
              aspernatur libero repellat quis consequatur ducimus quam nisi
              exercitationem omnis earum qui.
            </p>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="news">
          <div className="news__text">
            <h3 className="heading-tertiary u-margin-bottom-small">
              WOW! My life is completely different now
            </h3>
            <p>
              Lorem ipsum dolor sit amet consectetur adipisicing elit. Aperiam,
              ipsum sapiente aspernatur libero repellat quis consequatur ducimus
              quam nisi exercitationem omnis earum qui. Aperiam, ipsum sapiente
              aspernatur libero repellat quis consequatur ducimus quam nisi
              exercitationem omnis earum qui.
            </p>
          </div>
        </div>
      </div>
    </section> */}
    </Layout>
  );
};

export default IndexPage;
